import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Cell,
	Legend,
} from 'recharts'
import {
	getCategoryData,
	formatConversionAmount,
} from '../../utils/formatConversionData'

import type { CategoryConversionMetrics } from '../../types'

interface CategoryConversionChartProps {
	metrics: CategoryConversionMetrics | undefined
	isLoading?: boolean
}

export default function CategoryConversionChart({
	metrics,
	isLoading,
}: CategoryConversionChartProps) {
	const categories = getCategoryData(metrics)

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>ยอดฝากตามกลุ่ม</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 animate-pulse rounded bg-muted" />
				</CardContent>
			</Card>
		)
	}

	if (!metrics || categories.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>ยอดฝากตามกลุ่ม</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex h-64 items-center justify-center">
						<p className="text-muted-foreground">ไม่มีข้อมูล</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	const chartData = categories.map((cat) => ({
		name: cat.shortLabel,
		amount: cat.amount,
		count: cat.count,
		color: cat.color,
		fullName: cat.label,
	}))

	return (
		<Card>
			<CardHeader>
				<CardTitle>ยอดฝากตามกลุ่ม</CardTitle>
				<p className="text-sm text-muted-foreground">
					เปรียบเทียบยอดฝากจากผู้รับแต่ละกลุ่ม
				</p>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<BarChart
						data={chartData}
						margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
					>
						<CartesianGrid
							strokeDasharray="3 3"
							stroke="hsl(var(--border))"
							opacity={0.5}
						/>
						<XAxis
							dataKey="name"
							stroke="hsl(var(--muted-foreground))"
							tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
						/>
						<YAxis
							stroke="hsl(var(--muted-foreground))"
							tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
							tickFormatter={(value) => `฿${(value / 1000).toFixed(0)}k`}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: 'hsl(var(--card))',
								border: '1px solid hsl(var(--border))',
								borderRadius: '8px',
								boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
							}}
							formatter={(value: number, _name: string, props) => {
								return [formatConversionAmount(value), props.payload.fullName]
							}}
							labelFormatter={(label, payload) => {
								if (payload && payload[0]) {
									return `${payload[0].payload.fullName}: ${payload[0].payload.count} คน`
								}
								return label
							}}
						/>
						<Legend
							wrapperStyle={{ paddingTop: '10px' }}
							formatter={() => 'ยอดฝาก'}
						/>
						<Bar dataKey="amount" name="ยอดฝาก" radius={[4, 4, 0, 0]}>
							{chartData.map((entry, index) => (
								<Cell key={`cell-${index}`} fill={entry.color} />
							))}
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	)
}
